// frontend/src/components/homepage/useHeroCycle.js
//
// The hero's life: fill -> grow out of an ignited node -> count the score ->
// hold -> file away -> a beat of graph alone -> the next lead. That beat is
// BOUNDED (see MAX_DEAD_ZONE_MS below): the owner's rule is that a new card
// starts arriving within half a second of the last one leaving, so the hero
// never reads as finished.
//
// The prototype runs this as `while (true)` with bare `await sleep()`, which has
// no way to stop: unmounting the page, or a visitor turning animations off,
// leaves the loop running against detached nodes. Here the loop is the same
// shape but every wait goes through a CANCELLABLE ticker, and every await is
// followed by an `alive` check - so `stop()` both clears the pending timers and
// lets the loop fall out at its next checkpoint rather than being abandoned
// mid-flight.
//
// WHAT IS REACT STATE AND WHAT IS NOT, deliberately:
//   * CONTENT is React state - which lead, the displayed score, the heat flag.
//     It is what a crawler and a screen reader read.
//   * MOTION is imperative - the file-out class, the card's opacity, the Web
//     Animations entrance. Routing 60fps transform work through setState would
//     re-render the card on every frame, and MUI would re-serialise its whole sx
//     object each time.
//
// MOTION OFF / PRERENDER: the loop never starts. The card is mounted at its END
// state (filled, scored, stamped if hot) by HeroLive's initial state, so a still
// page shows a finished card rather than an empty one waiting for an animation
// that will not come.

import { useCallback, useEffect, useRef } from "react";
import { addTask } from "../../motion/rafCoordinator";
import { IGNITE_NEAR_EVENT } from "../../homepage/meshConstants";

/** How long the score spends counting up. */
export const SCORE_COUNT_MS = 1200;
/** How long a finished card stays on screen. */
export const HOLD_MS = 4600;
/**
 * THE DEAD-ZONE BUDGET (owner round 3, 2026-09-04).
 *
 * "A new lead card must appear within 0.5s of the previous one closing." The
 * stretch between the two is `SETTLE_MS + GAP + IGNITE_LEAD_MS`, and its
 * WORST case - every wait at its maximum - has to stay inside this number:
 *
 *   card closed -> the node ignites   = GAP + SETTLE            180-280ms
 *   the node ignites -> card grows    = IGNITE_LEAD_MS            +220ms
 *   ------------------------------------------------------------------
 *   card closed -> card grows                                  400-500ms
 *
 * Measured before this change, over five cycles at 1440: 1056 / 1817 / 1775 /
 * 1187 / 1283ms from close to ignite alone. The three constants below are
 * what shrank it, and `heroCycle.test.js` locks their sum against this
 * ceiling so a future "just a bit more air" cannot quietly reopen the gap.
 */
export const MAX_DEAD_ZONE_MS = 500;
/**
 * The pause after `fill` before anything moves. It is not decoration: React
 * commits the new lead's text in it, and `enterFromGraph` measures the card's
 * box straight afterwards. One frame's worth is enough; 250ms was not paid for.
 */
export const SETTLE_MS = 80;
/** The beat between the node igniting and the card starting to grow from it. */
export const IGNITE_LEAD_MS = 220;
/** The card's flight from node to rest. */
export const ENTRANCE_MS = 1100;
/** The file-down transition, matched to the CSS on .hl-lead. */
export const FILE_OUT_MS = 900;
/**
 * The graph-only gap between cards: a floor plus a random spread. Kept random
 * so the page does not tick like a metronome, but the whole range now fits
 * inside the budget above - the spread is what the visitor feels as variety,
 * not as a wait.
 */
export const GAP_MIN_MS = 100;
export const GAP_SPREAD_MS = 100;
/** The beat the HOT stamp gets to land before the hold starts. */
export const HOT_STAMP_MS = 300;

const EASE = "cubic-bezier(.22,1,.36,1)";

/**
 * A bundle of cancellable waits.
 *
 * `stop()` clears every pending timer AND resolves its promise, so the awaiting
 * loop resumes immediately and falls out at its next `alive` check. Leaving the
 * promises unresolved would strand each continuation until GC and make the
 * behaviour untestable with fake timers.
 */
export function createTicker() {
  let alive = true;
  /** @type {Map<any, () => void>} */
  const timers = new Map();
  /** @type {Set<() => void>} */
  const cancels = new Set();

  const sleep = (ms) =>
    new Promise((resolve) => {
      if (!alive) {
        resolve();
        return;
      }
      const id = setTimeout(() => {
        timers.delete(id);
        resolve();
      }, ms);
      timers.set(id, resolve);
    });

  return {
    sleep,
    isAlive: () => alive,
    /** Register an imperative cancel (a frame task, an animation). */
    onStop(cancel) {
      if (alive) cancels.add(cancel);
      else cancel();
      return () => cancels.delete(cancel);
    },
    stop() {
      if (!alive) return;
      alive = false;
      timers.forEach((resolve, id) => {
        clearTimeout(id);
        resolve();
      });
      timers.clear();
      cancels.forEach((cancel) => {
        try {
          cancel();
        } catch {
          /* one bad cancel must not strand the rest */
        }
      });
      cancels.clear();
    },
  };
}

/**
 * Count a number up on the shared frame chain.
 *
 * On rafCoordinator rather than its own rAF for the usual reason - one chain per
 * page - and cubic ease-out because a linear count reads as a progress bar
 * rather than a machine arriving at an answer.
 *
 * @returns {Promise<void>} resolves when the count reaches the target (or when
 *   the ticker is stopped).
 */
export function countUp(target, durationMs, onValue, ticker) {
  return new Promise((resolve) => {
    if (!ticker.isAlive() || durationMs <= 0) {
      onValue(target);
      resolve();
      return;
    }
    let t0 = null;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      remove();
      release();
      resolve();
    };
    const remove = addTask((dtMs, nowMs) => {
      // Elapsed is measured on the frame clock, not summed from the capped
      // per-frame dt: a dropped frame must not stretch the 1.2s count.
      if (t0 === null) t0 = nowMs;
      const p = Math.min(1, (nowMs - t0) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      onValue(Math.round(target * eased));
      if (p >= 1) finish();
    });
    // If motion is switched off mid-count the chain is cancelled entirely and
    // this task will never run again - so the stop hook has to be what finishes
    // it, or the cycle would hang here forever.
    const release = ticker.onStop(() => {
      onValue(target);
      finish();
    });
  });
}

/**
 * Ask the mesh for a node near a point and light it.
 *
 * The answer comes back on the event's own detail object, synchronously,
 * because MeshCanvas answers in its listener. No mesh mounted (mobile fallback,
 * prerender, a canvas with no context) simply means no result, and the caller
 * falls back to the point it asked about.
 *
 * @returns {{x: number, y: number}} always a usable point.
 */
export function requestIgnition(x, y) {
  const fallback = { x, y };
  if (typeof window === "undefined" || typeof window.CustomEvent !== "function") {
    return fallback;
  }
  const event = new CustomEvent(IGNITE_NEAR_EVENT, { detail: { x, y } });
  window.dispatchEvent(event);
  const result = event.detail.result;
  return result && Number.isFinite(result.x) && Number.isFinite(result.y) ? result : fallback;
}

/**
 * Drive the hero card through its cycle.
 *
 * @param {object} options
 * @param {boolean} options.enabled run at all (false = motion off / prerender /
 *   the hero is off screen).
 * @param {boolean} options.reduced skip the flight and the burst - a phone, or
 *   any device where the entrance is not worth the frame budget.
 * @param {object[]} options.leads
 * @param {number} options.hotScore
 * @param {object} options.refs `{ front, card, stage }` element refs.
 * @param {object} options.actions `{ setLead, setScore, setHot, showBurst }`.
 */
export default function useHeroCycle({ enabled, reduced, leads, hotScore, refs, actions }) {
  // The callers are re-created on every render; the loop must read the CURRENT
  // ones without being restarted, or the cycle would tear down and rebuild
  // itself on each state change it makes.
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const refsRef = useRef(refs);
  refsRef.current = refs;

  const random = useCallback(() => Math.random(), []);

  useEffect(() => {
    if (!enabled || !leads || leads.length === 0) return undefined;

    const ticker = createTicker();
    let index = 0;

    /** Put the next lead's content on the card, un-scored and un-stamped. */
    const fill = (lead) => {
      const { setLead, setScore, setHot } = actionsRef.current;
      setLead(lead);
      setScore(0);
      setHot(false);
      const front = refsRef.current.front?.current;
      const card = refsRef.current.card?.current;
      if (front) front.classList.remove("hl-lead-out");
      if (card) card.style.opacity = "0";
    };

    /**
     * Grow the card out of a node in the graph.
     *
     * The target is a random point ACROSS the top of the stage rather than a
     * fixed one, so two consecutive cards never arrive from the same place -
     * that repetition is what makes a loop look like a loop.
     */
    const enterFromGraph = async () => {
      const card = refsRef.current.card?.current;
      const stage = refsRef.current.stage?.current;
      if (!card) return;

      const stageRect = stage?.getBoundingClientRect();
      const target = stageRect
        ? {
            x: stageRect.left + stageRect.width * (0.1 + random() * 0.8),
            y: stageRect.top - 30 + random() * 70,
          }
        : { x: 0, y: 0 };
      const point = requestIgnition(target.x, target.y);

      if (reduced || typeof card.animate !== "function") {
        card.style.opacity = "1";
        return;
      }

      actionsRef.current.showBurst(point);
      const cardRect = card.getBoundingClientRect();
      const dx = point.x - (cardRect.left + cardRect.width / 2);
      const dy = point.y - (cardRect.top + cardRect.height / 2);

      // The node flares first and the card follows it: cause, then effect.
      await ticker.sleep(IGNITE_LEAD_MS);
      if (!ticker.isAlive()) return;

      const animation = card.animate(
        [
          { transform: `translate(${dx}px,${dy}px) scale(.06)`, opacity: 0 },
          {
            transform: `translate(${dx * 0.55}px,${dy * 0.55}px) scale(.42)`,
            opacity: 1,
            offset: 0.4,
          },
          { transform: "translate(0,0) scale(1)", opacity: 1 },
        ],
        { duration: ENTRANCE_MS, easing: EASE }
      );
      const release = ticker.onStop(() => {
        try {
          animation.cancel();
        } catch {
          /* already finished */
        }
      });
      try {
        await animation.finished;
      } catch {
        // A cancelled animation rejects. That is a stop, not a failure.
      }
      release();
      if (!ticker.isAlive()) return;
      card.style.opacity = "1";
    };

    const run = async () => {
      while (ticker.isAlive()) {
        const lead = leads[index % leads.length];

        fill(lead);
        await ticker.sleep(SETTLE_MS);
        if (!ticker.isAlive()) return;

        await enterFromGraph();
        if (!ticker.isAlive()) return;

        await countUp(
          lead.score,
          reduced ? 0 : SCORE_COUNT_MS,
          (value) => actionsRef.current.setScore(value),
          ticker
        );
        if (!ticker.isAlive()) return;

        if (lead.score >= hotScore) {
          actionsRef.current.setHot(true);
          await ticker.sleep(HOT_STAMP_MS);
          if (!ticker.isAlive()) return;
        }

        await ticker.sleep(HOLD_MS);
        if (!ticker.isAlive()) return;

        // File the finished card down and out, then leave the graph alone for a
        // beat. The gap is randomised so the page does not tick like a
        // metronome, but it is bounded by MAX_DEAD_ZONE_MS: the graph alone is
        // a breath between cards, never a stretch where the hero looks dead.
        const front = refsRef.current.front?.current;
        if (front) front.classList.add("hl-lead-out");
        await ticker.sleep(FILE_OUT_MS);
        if (!ticker.isAlive()) return;
        const card = refsRef.current.card?.current;
        if (card) card.style.opacity = "0";
        await ticker.sleep(GAP_MIN_MS + random() * GAP_SPREAD_MS);
        if (!ticker.isAlive()) return;

        index += 1;
      }
    };

    run();

    return () => {
      ticker.stop();
      // Hand the card back in a sane state: a stopped cycle must leave a
      // readable card, never a half-flown one.
      const front = refsRef.current.front?.current;
      const card = refsRef.current.card?.current;
      if (front) front.classList.remove("hl-lead-out");
      if (card) {
        card.style.opacity = "1";
        if (typeof card.getAnimations === "function") {
          card.getAnimations().forEach((animation) => animation.cancel());
        }
      }
    };
  }, [enabled, reduced, leads, hotScore, random]);
}
